import { PETS_BY_ID } from '@/config/pets.config';
import { EGGS_BY_ID } from '@/config/eggs.config';
import { petAssetUrl, eggAssetUrl } from '@/assets/paths';
import { VariantMark } from './PetCollectionFilters';
import type { Variant } from '@/domain/types/common';
import type { StallSale } from '@/ui/stallSales';
import { formatNumberCompact } from '@/utils/formatNumber';
import { gemWord } from '@/utils/pluralPl';
import { Icon } from './Icon';

/** Read-only snapshot: no inventory lookup, equip/merge actions, or fake pet UID. */
export function StallSaleCard({ sale, compact = false }: { sale: StallSale; compact?: boolean }): JSX.Element {
  const pet = sale.kind === 'pet' && sale.def_id ? PETS_BY_ID[sale.def_id] : undefined;
  const egg = sale.kind === 'eggs' && sale.def_id ? EGGS_BY_ID[sale.def_id] : undefined;
  const image = pet ? petAssetUrl(pet.art.textureKey, pet.origin.biomeId ?? 'biome-meadow')
    : egg ? eggAssetUrl(egg.art.textureKey) : '/ui/ui_btn_pets.webp';
  const name = pet?.name.pl ?? egg?.name.pl ?? (sale.kind === 'pet' ? 'Pet' : 'Jajka');
  const date = new Date(sale.created_at);
  const variant = ['normal', 'gold', 'rainbow', 'huge', 'titanic'].includes(sale.variant) ? sale.variant as Variant : 'normal';
  return (
    <div className={`rounded-2xl border-2 border-meadow-200 bg-white ${compact ? 'p-2.5' : 'p-3'}`}>
      <div className={`grid ${compact ? 'grid-cols-[3rem_0.75rem_minmax(0,1fr)]' : 'grid-cols-[4rem_0.75rem_minmax(0,1fr)]'} items-center gap-x-2 gap-y-1 sm:grid-cols-[4rem_1rem_minmax(0,1fr)_auto] sm:gap-x-3`}>
        <div className="relative shrink-0 rounded-2xl bg-meadow-50 p-1">
          <Icon key={image} src={image} fallback="" fallbackSrc="/ui/ui_btn_pets.webp" alt={name} className={`${compact ? 'h-12 w-12' : 'h-16 w-16'} object-contain`} />
          {sale.quantity > 1 && <span className="absolute -bottom-1 -right-1 rounded-full bg-meadow-700 px-1.5 text-xs font-bold text-white">×{sale.quantity}</span>}
        </div>
        <Icon src="/ui/ui_arrow_left.webp" fallback="" alt="" className="h-4 w-4 rotate-180" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <Icon src="/ui/ui_btn_account.webp" fallback="" alt="" className="h-7 w-7 shrink-0" />
            <div className="min-w-0">
              <span className="block text-[10px] font-bold text-meadow-600">Kupił</span>
              <strong className="block break-all text-sm text-meadow-800">{sale.buyer_display_name}</strong>
            </div>
          </div>
          {!compact && <p className="mt-1 break-words text-xs text-gray-600">{name}</p>}
          {pet && variant !== 'normal' && <VariantMark value={variant} />}
        </div>
        <div className="col-start-3 text-left sm:col-start-4 sm:row-start-1 sm:text-right">
          <span className="inline-flex items-center gap-1 rounded-xl bg-purple-100 px-2 py-2 text-sm font-extrabold text-purple-700" aria-label={`Otrzymano ${sale.net_crystals} ${gemWord(sale.net_crystals)}`}>
            +{formatNumberCompact(sale.net_crystals)}
            <Icon src="/ui/ui_gem.webp" fallback="" alt="gemy" className="h-5 w-5" />
          </span>
        </div>
      </div>
      {!compact && <div className="mt-2 flex flex-wrap items-center justify-between gap-1 text-[10px] text-gray-500">
        <time dateTime={sale.created_at}>{Number.isNaN(date.getTime()) ? '—' : date.toLocaleString('pl-PL', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</time>
        {sale.commission_crystals !== null && sale.commission_crystals > 0 && <span>Cena {sale.price_crystals ?? '—'} · Prowizja {sale.commission_crystals}</span>}
      </div>}
    </div>
  );
}
